import { AddToPhotos, Delete } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { DeleteModal } from 'components/Dialog';
import { CloudFlareUploader } from "components/UI";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { closeModal } from "utils/assets/images";
const style = {};

const isCloudflareStreamUrl = (url) =>
  String(url || '').includes('cloudflarestream.com');

const isValidVideoUrl = (url) => {
    try {
        if (!url || typeof url !== 'string') {
            return false;
        }
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return false;
        }
        const lowerUrl = trimmedUrl.toLowerCase();
        let urlToCheck = trimmedUrl;
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            urlToCheck = `https://${trimmedUrl}`;
        }
        try {
            new URL(urlToCheck);
        } catch {
            return false;
        }
        if (lowerUrl.includes('cloudflarestream.com') || lowerUrl.includes('.m3u8')) {
            return true;
        }
        let decodedUrl = lowerUrl;
        try {
            decodedUrl = decodeURIComponent(lowerUrl).toLowerCase();
        } catch {
            decodedUrl = lowerUrl;
        }
        const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.flv', '.wmv', '.m4v', '.3gp', '.ogv'];
        if (videoExtensions.some((ext) => lowerUrl.includes(ext) || decodedUrl.includes(ext))) {
            return true;
        }
        if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be') || lowerUrl.includes('youtube-nocookie.com')) {
            return true;
        }
        if (lowerUrl.includes('vimeo.com')) {
            return true;
        }
        return false;
    } catch (error) {
        Sentry.captureException(error);
        return false;
    }
};


const ModalShell = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 48px 20px 10px;
  max-width: min(600px, 94vw);
  width: 100%;
  margin: 0 auto;
  max-height: 88vh;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
`;

const ModalHeaderActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseWrap = styled.div`
  cursor: pointer;
  line-height: 0;
  flex-shrink: 0;
`;

const ReuploadButton = styled(Button)`
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.7) !important;
  color: #03989e !important;
  border: 1px solid #03989e !important;
  text-transform: none;
  font-size: 12px;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: #03989e !important;
  }

  &:disabled {
    color: #03989e !important;
    border-color: #03989e !important;
    opacity: 0.7;
  }

  .MuiButton-startIcon,
  .MuiSvgIcon-root {
    color: #03989e !important;
  }
`;

const DeleteWrap = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
`;

const ModalBody = styled.div`
  position: relative;
  width: 100%;
`;

const VideoContainer = styled.div`
  width: 100%;
  margin-bottom: 0px;
`;

const UploaderStage = styled.div`
  position: relative;
  isolation: isolate;
  padding-bottom: 28px;
`;

const VideoUploaderNonNativeAdmin = ({
  videoUrl,
  openVideoModal,
  handleClose,
  pageId = "",
  getUrl,
  modalZIndex = 1400,
  hideModalBackdrop = false,
}) => {
  const showAlert = useAlert();
  const [source, setSource] = useState(videoUrl);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [waitingForVideoUrl, setWaitingForVideoUrl] = useState(false);
  const [encodingInProgress, setEncodingInProgress] = useState(false);
  const [hasSavedVideoUrl, setHasSavedVideoUrl] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const videoRef = useRef();
  const cloudflareUploaderRef = useRef(null);

  const handleCloudflareUploadBusy = useCallback((busy) => {
    setUploadInProgress(Boolean(busy));
    if (!busy) {
      setWaitingForVideoUrl(false);
    }
  }, []);

  const handleUploadStart = useCallback(() => {
    setHasSavedVideoUrl(false);
    setWaitingForVideoUrl(true);
    setUploadInProgress(true);
    setEncodingInProgress(false);
  }, []);

  const handleEncodingChange = useCallback((encoding) => {
    setEncodingInProgress(Boolean(encoding));
  }, []);

  useEffect(() => {
    const nextSource = String(videoUrl ?? '');
    setSource(nextSource);
    const hasUrl = Boolean(nextSource.trim());
    setHasSavedVideoUrl(hasUrl);
    if (hasUrl) {
      setWaitingForVideoUrl(false);
      if (isCloudflareStreamUrl(nextSource)) {
        setEncodingInProgress(true);
      }
    }
  }, [videoUrl]);

  useEffect(() => {
    if (source && videoRef.current) {
      videoRef.current.load();
    }
  }, [source]);

  useEffect(() => {
    if (!openVideoModal) {
      setShowUrlInput(false);
      setUrlInput('');
      setUploadInProgress(false);
      setWaitingForVideoUrl(false);
      setEncodingInProgress(false);
      setOpenDeleteConfirm(false);
    }
  }, [openVideoModal]);

  const isWaitingForUrl = waitingForVideoUrl && !hasSavedVideoUrl;

  const tryCloseModal = (event) => {
    event?.stopPropagation?.();
    if (isWaitingForUrl) {
      showAlert('Upload in progress. Please wait until the video URL is ready.', 'error');
      return;
    }
    handleClose();
  };

  const tryDismissModal = (_event, reason) => {
    if (reason === 'backdropClick' && isWaitingForUrl) {
      showAlert('Upload in progress. Please wait until the video URL is ready.', 'error');
      return;
    }
    if (isWaitingForUrl) {
      return;
    }
    handleClose();
  };

  const handleAddVideoWithUrl = () => {
    setShowUrlInput(true);
    setUrlInput('');
  };

  const cancelUrlInput = () => {
    setShowUrlInput(false);
    setUrlInput('');
  };

  const handleUrlSubmit = () => {
    try {
      const trimmedUrl = urlInput.trim();
      if (!trimmedUrl) {
        showAlert('Please enter a valid URL', 'error');
        return;
      }
      if (!isValidVideoUrl(trimmedUrl)) {
        showAlert('Invalid URL. Please enter a valid video URL.', 'error');
        return;
      }
      setShowUrlInput(false);
      setUrlInput('');
      setHasSavedVideoUrl(true);
      setWaitingForVideoUrl(false);
      setUploadInProgress(false);
      getUrl(trimmedUrl);
      setSource(trimmedUrl);
      showAlert('Video URL added successfully!', 'success');
    } catch (error) {
      Sentry.captureException(error);
      showAlert('Failed to add video URL. Please try again.', 'error');
    }
  };

  const hasActiveVideo = Boolean(String(source || videoUrl || '').trim());
  const showUploadOverlay = (uploadInProgress || waitingForVideoUrl) && !hasSavedVideoUrl;
  const showEncodingOverlay = encodingInProgress;
  const handleDeleteVideo = () => {
    setHasSavedVideoUrl(false);
    setWaitingForVideoUrl(false);
    setUploadInProgress(false);
    setEncodingInProgress(false);
    setShowUrlInput(false);
    setUrlInput('');
    setSource('');
    getUrl(null);
  };

  const handleDeleteClick = () => {
    setOpenDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteConfirm(false);
  };

  const handleDeleteConfirm = () => {
    handleDeleteVideo();
    setOpenDeleteConfirm(false);
  };

  return (
    <>
      <Modal
        open={openVideoModal}
        hideBackdrop={hideModalBackdrop}
        onClose={tryDismissModal}
        disableEscapeKeyDown={isWaitingForUrl}
        className="modelCntnt"
        sx={{ zIndex: modalZIndex }}
      >
        <Box
          sx={{
            ...style,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            maxWidth: 640,
            outline: 'none',
            maxHeight: '100vh',
            overflow: openVideoModal ? 'auto' : 'hidden',
            padding: 2,
            boxSizing: 'border-box',
          }}
        >
          <ModalShell>
          {hasActiveVideo ? (
            <DeleteWrap>
              <IconButton
                style={{ backgroundColor: 'red', height: 30, width: 30 }}
                onClick={handleDeleteClick}
              >
                <Delete style={{ color: '#fff' }} />
              </IconButton>
            </DeleteWrap>
          ) : null}
          <ModalHeaderActions>
            {hasActiveVideo ? (
              <ReuploadButton
                variant="outlined"
                size="small"
                startIcon={<AddToPhotos />}
                onClick={() => cloudflareUploaderRef.current?.openReuploadPicker()}
                disabled={uploadInProgress || encodingInProgress}
              >
                Re-upload
              </ReuploadButton>
            ) : null}
            <CloseWrap
              onClick={tryCloseModal}
              onMouseDown={(event) => event.stopPropagation()}
              role="button"
              aria-label="Close"
            >
              <img
                style={{ height: '30px', pointerEvents: 'none' }}
                src={closeModal}
                alt=""
              />
            </CloseWrap>
          </ModalHeaderActions>
          <ModalBody>
            <VideoContainer>
              <Stack spacing={2}>
                <UploaderStage>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      opacity: showUploadOverlay || showEncodingOverlay ? 0 : 1,
                      pointerEvents: showUploadOverlay || showEncodingOverlay ? 'none' : 'auto',
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <CloudFlareUploader
                      ref={cloudflareUploaderRef}
                      headerReupload
                      key={`cf-${pageId}-${videoUrl ? String(videoUrl).slice(-64) : 'empty'}`}
                      pageId={pageId}
                      src={String(source || videoUrl || '')}
                      onUploadStart={handleUploadStart}
                      onUploadBusyChange={handleCloudflareUploadBusy}
                      onEncodingChange={handleEncodingChange}
                      onChange={(v) => {
                        const next = v?.url;
                        if (next) {
                          setHasSavedVideoUrl(true);
                          setWaitingForVideoUrl(false);
                          setUploadInProgress(false);
                          setSource(String(next));
                          if (isCloudflareStreamUrl(next)) {
                            setEncodingInProgress(true);
                          }
                        }
                        getUrl(next);
                      }}
                    />
                  </Box>
                  {showUploadOverlay || showEncodingOverlay ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 28,
                        zIndex: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.82)',
                        color: 'common.white',
                        borderRadius: 1,
                        textAlign: 'center',
                        px: 2,
                        py: 2,
                      }}
                    >
                      <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 320 }}>
                        <CircularProgress size={40} sx={{ color: 'common.white' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {showEncodingOverlay ? 'Preparing video for playback…' : 'Uploading video…'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {showEncodingOverlay
                            ? 'Your video URL is saved. Cloudflare is encoding the video — you can close this dialog and check back in a minute.'
                            : 'Please wait until the video URL is ready. You can close after that; Cloudflare encoding continues in the background.'}
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null}
                </Box>
                </UploaderStage>
                <Box sx={{ pt: 0.5 }}>
                  {!hasActiveVideo ?
                  !showUrlInput ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddToPhotos />}
                      onClick={handleAddVideoWithUrl}
                    >
                      Add video URL
                    </Button>
                  ) : (
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Paste video URL here"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUrlSubmit();
                          }
                        }}
                        autoFocus
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fafafa',
                          },
                        }}
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={cancelUrlInput}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={handleUrlSubmit}>
                          OK
                        </Button>
                      </Stack>
                    </Stack>
                  ):null}
                </Box>
              </Stack>
            </VideoContainer>
          </ModalBody>
          </ModalShell>
        </Box>
      </Modal>
      <DeleteModal
        message="This will remove the current video from this item."
        deleteToggle={openDeleteConfirm}
        setDeleteToggle={setOpenDeleteConfirm}
        loading={false}
        buttonTitle="Delete"
        onDelete={handleDeleteConfirm}
        onClickCancel={handleDeleteCancel}
        modalZIndex={modalZIndex + 100}
      />
    </>
  );
};

export default VideoUploaderNonNativeAdmin;
